import React from 'react';
import { ChevronDown, ChevronUp, Key, Laptop, Globe, Layers, Database, Eye, Cog, Shield, CheckCircle2 } from 'lucide-react';
import CisaGapBar from './CisaGapBar';
import CisaProductChip from './CisaProductChip';
import CisaProductPicker from './CisaProductPicker';
import {
  MATURITY_CRITERIA,
  getConfigurationSteps,
  getNextLevel
} from '../config/cisaMaturityModel';

const LEVELS = ['TRADITIONAL', 'INITIAL', 'ADVANCED', 'OPTIMAL'];
const LEVEL_LABELS = {
  TRADITIONAL: 'Traditional',
  INITIAL: 'Initial',
  ADVANCED: 'Advanced',
  OPTIMAL: 'Optimal'
};
const LEVEL_COLORS = {
  TRADITIONAL: '#6b7280',
  INITIAL: '#eab308',
  ADVANCED: '#3b82f6',
  OPTIMAL: '#10b981'
};

const PILLAR_ICONS = {
  IDENTITY: Key,
  DEVICES: Laptop,
  NETWORKS: Globe,
  APPLICATIONS: Layers,
  DATA: Database,
  VISIBILITY: Eye,
  AUTOMATION: Cog,
  GOVERNANCE: Shield
};

/**
 * CisaFunctionCard - Expandable card for a single CISA function
 *
 * Props:
 *   functionCode - e.g., 'ID_AUTH'
 *   functionName - e.g., 'Authentication'
 *   pillarCode - e.g., 'IDENTITY'
 *   pillarColor - hex color for the pillar
 *   currentLevel - current maturity level
 *   targetLevel - target maturity level (optional)
 *   adoptedProducts - array of { productName, status }
 *   expanded - boolean
 *   onToggleExpand - () => void
 *   onCurrentLevelChange - (level) => void
 *   onTargetLevelChange - (level) => void
 *   onAdoptProduct - (productName, status) => void
 *   onUpdateProductStatus - (productName, newStatus) => void
 *   onRemoveProduct - (productName) => void
 */
const CisaFunctionCard = ({
  functionCode,
  functionName,
  pillarCode,
  pillarColor,
  currentLevel = 'TRADITIONAL',
  targetLevel,
  adoptedProducts = [],
  expanded = false,
  onToggleExpand,
  onCurrentLevelChange,
  onTargetLevelChange,
  onAdoptProduct,
  onUpdateProductStatus,
  onRemoveProduct
}) => {
  const Icon = PILLAR_ICONS[pillarCode] || Key;
  const productCount = adoptedProducts.length;
  const effectiveTarget = targetLevel || currentLevel;
  const nextLevel = getNextLevel(currentLevel);
  const configSteps = nextLevel ? getConfigurationSteps(functionCode, nextLevel) : [];

  return (
    <div
      className={`cisa-function-card ${expanded ? 'cisa-function-card--expanded' : ''}`}
      style={{ '--card-pillar-color': pillarColor }}
    >
      {/* Collapsed header - always visible */}
      <button
        type="button"
        className="cisa-function-card-header"
        onClick={onToggleExpand}
        aria-expanded={expanded}
      >
        <div className="cisa-function-card-header-left">
          <span className="cisa-function-card-pillar-badge" style={{ background: pillarColor }}>
            <Icon size={12} />
          </span>
          <span className="cisa-function-card-name">{functionName}</span>
        </div>

        <div className="cisa-function-card-header-right">
          <CisaGapBar
            currentLevel={currentLevel}
            targetLevel={effectiveTarget}
            compact
          />
          {productCount > 0 && (
            <span className="cisa-function-card-product-count">
              {productCount} product{productCount !== 1 ? 's' : ''}
            </span>
          )}
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </button>

      {/* Expanded content */}
      {expanded && (
        <div className="cisa-function-card-body">
          {/* Current Level Section */}
          <div className="cisa-function-card-section">
            <div className="cisa-function-card-section-header">
              <span className="cisa-function-card-section-label">Current Level</span>
              <span
                className="cisa-function-card-level-badge"
                style={{ background: `${LEVEL_COLORS[currentLevel]}20`, color: LEVEL_COLORS[currentLevel] }}
              >
                {LEVEL_LABELS[currentLevel]}
              </span>
            </div>
            <p className="cisa-function-card-criteria">
              {MATURITY_CRITERIA[functionCode]?.[currentLevel] || 'No criteria defined'}
            </p>
          </div>

          {/* Target Level Selector */}
          <div className="cisa-function-card-section">
            <div className="cisa-function-card-section-header">
              <span className="cisa-function-card-section-label">Target Level</span>
            </div>
            <div className="cisa-function-card-level-selector">
              {LEVELS.map((level) => {
                const isSelected = effectiveTarget === level;
                const isCurrent = currentLevel === level;
                const levelColor = LEVEL_COLORS[level];
                return (
                  <button
                    key={level}
                    type="button"
                    className={`cisa-function-card-level-btn ${isSelected ? 'selected' : ''} ${isCurrent ? 'current' : ''}`}
                    style={{
                      '--level-color': levelColor,
                      '--level-bg': isSelected ? levelColor : 'transparent'
                    }}
                    onClick={() => onTargetLevelChange?.(level)}
                  >
                    {LEVEL_LABELS[level].slice(0, 4).toUpperCase()}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Progress Gap Bar */}
          <div className="cisa-function-card-section">
            <div className="cisa-function-card-section-header">
              <span className="cisa-function-card-section-label">Progress</span>
              {effectiveTarget !== currentLevel && (
                <span className="cisa-function-card-gap-label">
                  {LEVEL_LABELS[currentLevel]} → {LEVEL_LABELS[effectiveTarget]}
                </span>
              )}
            </div>
            <CisaGapBar
              currentLevel={currentLevel}
              targetLevel={effectiveTarget}
              showLabels
            />
          </div>

          {/* Products Section */}
          {nextLevel && (
            <div className="cisa-function-card-section">
              <div className="cisa-function-card-section-header">
                <span className="cisa-function-card-section-label">
                  Products for {LEVEL_LABELS[nextLevel]}
                </span>
              </div>
              <CisaProductPicker
                functionCode={functionCode}
                maturityLevel={nextLevel}
                adoptedProducts={adoptedProducts}
                onAdopt={onAdoptProduct}
              />
            </div>
          )}

          {/* Adopted Products */}
          {adoptedProducts.length > 0 && (
            <div className="cisa-function-card-section">
              <div className="cisa-function-card-section-header">
                <span className="cisa-function-card-section-label">Adopted Products</span>
              </div>
              <div className="cisa-function-card-products">
                {adoptedProducts.map((product) => (
                  <CisaProductChip
                    key={product.productName}
                    productName={product.productName}
                    status={product.status}
                    onStatusChange={(newStatus) => onUpdateProductStatus?.(product.productName, newStatus)}
                    onRemove={() => onRemoveProduct?.(product.productName)}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Configuration Steps */}
          {configSteps.length > 0 && (
            <div className="cisa-function-card-section cisa-function-card-section--config">
              <div className="cisa-function-card-section-header">
                <span className="cisa-function-card-section-label">
                  Configuration Steps for {LEVEL_LABELS[nextLevel]}
                </span>
              </div>
              <ul className="cisa-function-card-config-list">
                {configSteps.map((step, idx) => (
                  <li key={idx} className="cisa-function-card-config-item">
                    <CheckCircle2 size={14} className="cisa-function-card-config-icon" />
                    <span>{step}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Current Level Selector (for updating assessment) */}
          <div className="cisa-function-card-section cisa-function-card-section--assess">
            <div className="cisa-function-card-section-header">
              <span className="cisa-function-card-section-label">Update Current Assessment</span>
            </div>
            <div className="cisa-function-card-assess-buttons">
              {LEVELS.map((level) => {
                const isSelected = currentLevel === level;
                const levelColor = LEVEL_COLORS[level];
                return (
                  <button
                    key={level}
                    type="button"
                    className={`cisa-function-card-assess-btn ${isSelected ? 'selected' : ''}`}
                    style={{
                      '--level-color': levelColor,
                      background: isSelected ? levelColor : 'transparent',
                      color: isSelected ? (level === 'INITIAL' ? '#000' : '#fff') : levelColor,
                      borderColor: levelColor
                    }}
                    onClick={() => onCurrentLevelChange?.(level)}
                  >
                    {LEVEL_LABELS[level]}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CisaFunctionCard;
